import React from "react";

export interface LinkCardItem {
    icon: React.ReactNode;
    label: string;
    title: string;
    desc?: string;
    href?: string;
    onClick?: () => void;
    accentClass: string;
    iconBgClass: string;
    iconColorClass: string;
    highlight?: boolean;
}

function ArrowIcon() {
    return (
        <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="w-4 h-4"
        >
            <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
    );
}

interface LinkCardGridProps {
    cards: LinkCardItem[];
    className?: string;
    columnsClassName?: string;
}

export default function LinkCardGrid({
    cards,
    className = "",
    columnsClassName = "grid grid-cols-2 gap-4 my-4 max-w-4xl",
}: LinkCardGridProps) {
    return (
        <div className={`${columnsClassName} ${className}`.trim()}>
            {cards.map((card, i) => {
                const isHighlighted = !!card.highlight;

                const sharedClassName = [
                    "group relative flex flex-col rounded-xl p-4 overflow-hidden no-underline",
                    "transition-all duration-200 hover:-translate-y-1",
                    isHighlighted
                        ? "bg-port-blue shadow-lg hover:shadow-xl"
                        : "bg-white border border-slate-200 shadow-sm hover:shadow-md",
                ].join(" ");

                const content = (
                    <>
                        <div className={`absolute top-0 left-0 right-0 h-0.5 ${card.accentClass}`} />

                        <div
                            className={[
                                "inline-flex items-center justify-center w-8 h-8 rounded-lg my-1",
                                card.iconBgClass,
                                card.iconColorClass,
                            ].join(" ")}
                        >
                            {card.icon}
                        </div>

                        <span
                            className={[
                                "text-xs uppercase tracking-widest font-medium mb-1",
                                isHighlighted ? "text-white/50" : "text-slate-400",
                            ].join(" ")}
                        >
                            {card.label}
                        </span>

                        <h3
                            className={[
                                "text-base font-bold leading-snug mb-2 flex items-center gap-1.5",
                                isHighlighted ? "text-white" : "text-port-blue",
                            ].join(" ")}
                        >
                            <span>{card.title}</span>
                            <span className="flex-shrink-0"><ArrowIcon /></span>
                        </h3>
                    </>
                );

                if (card.href) {
                    return (
                        <a
                            key={i}
                            href={card.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={sharedClassName}
                        >
                            {content}
                        </a>
                    );
                }

                return (
                    <button
                        key={i}
                        type="button"
                        onClick={card.onClick}
                        className={`${sharedClassName} text-left`}
                    >
                        {content}
                    </button>
                );
            })}
        </div>
    );
}